import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SearchSelect from '../components/shared/SearchSelect';
import { EngagementBlockers, engagementBlockReasons } from '../components/workflow/RecordBlockers';
import { Field, FilePicker, InfoTip, inputCls } from '../components/shared/FormKit';
import {
  FormValidationProvider, useFormValidation, useFieldInvalid, FieldInvalidInline, INVALID_FIELD_ATTR,
} from '../components/shared/formValidation';
import { fieldInvalidRing, fieldInvalidLabel, detailLabel, fileChipRemove } from '../components/workflow/constants';
import { FormShell, FormSection } from '../components/workflow/FormShell';
import ReviewerSelect from '../components/workflow/ReviewerSelect';
import { shortEngagementName } from '../components/workflow/CoverageDisplay';
import { engagementTag } from '../utils/engagementName';
import { FieldCommentInline } from '../components/workflow/fieldComments';
import { useFsAutoDetect } from '../components/workflow/FsDateAutoDetect';
import { useFsAiReview } from '../components/workflow/FsAiReview';
import { formatDate, formatDateShort } from '../utils/dateFormat';
import { fetchJSON, API_BASE_URL } from '../utils/api';
import { hintOf } from '../utils/uploadKinds';

// Sign-off date is usually unchanged — an amendment opts in only when it must
// move. The toggle + date input live here once and are shared with the Detail
// page's Modify modal（engreg 模式）：兩面同一個開關，退回後才收得回一個
// 已經填了的 revised date。Unticking clears the date, so a saved blank means
// "keep the current sign-off". The comment anchor keys `signingDate`, the
// same key the diff tables use — and the same key the required mark uses when
// the switch is on but the date is still empty (this block is not a Field, so
// it marks itself the way one would).
//
// `detect` is the "Auto-detect from PDF" hook (useFsAutoDetect) built on the
// corrected document picked on the same form: the button sits where a Field's
// action slot would, and its outcome panel lands under the date input. A hit
// flips the switch on and fills the date (the caller wires that in
// onDetected), so a scan is enough to propose the revised sign-off; the other
// outcomes only report, leaving the switch as it was.
export const ReviseSignOffField = ({ reviseSignOff, onToggle, signingDate, onSigningDateChange, currentSignOffDate, detect = null }) => {
  const invalid = useFieldInvalid('signingDate');
  return (
    <div className={`rounded-lg border border-slate-200 p-3 ${invalid ? fieldInvalidRing : ''}`} {...(invalid ? { [INVALID_FIELD_ATTR]: 'signingDate' } : {})}>
      <div className="flex items-start justify-between gap-3">
        <label className={`flex items-start gap-2 text-sm ${invalid ? fieldInvalidLabel : 'text-slate-700'}`}>
          <input
            type="checkbox"
            className="mt-0.5"
            checked={reviseSignOff}
            onChange={(e) => {
              const on = e.target.checked;
              onToggle(on);
              if (!on) onSigningDateChange('');
            }}
          />
          <span>
            Also revise the sign-off date
            <span className="block text-xs text-slate-500 font-normal">
              Leave unticked to keep the current sign-off{currentSignOffDate ? ` (${formatDate(currentSignOffDate)})` : ''}. Ticking re-stamps it on approval.
            </span>
          </span>
        </label>
        {detect && <div className="shrink-0">{detect.button}</div>}
      </div>
      {reviseSignOff && (
        <input
          type="date"
          value={signingDate}
          onChange={(e) => onSigningDateChange(e.target.value)}
          className={`${inputCls} mt-2`}
        />
      )}
      {detect?.panel}
      <FieldInvalidInline field="signingDate" text="Required — enter the revised signing date, or untick the switch" />
      <FieldCommentInline field="signingDate" />
    </div>
  );
};

// Signed Financial Statements Amendment — submission form (docs/modules/workflow-fs.md §6).
// fs-only (QC35 corrections → /workflow/qc35a).
const WorkflowFsaNew = () => {
  const navigate = useNavigate();

  const [engagements, setEngagements] = useState([]);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [engagementId, setEngagementId] = useState('');
  const [reviseSignOff, setReviseSignOff] = useState(false);
  const [signingDate, setSigningDate] = useState('');
  const [file, setFile] = useState(null);
  // Local object URL so the freshly picked replacement is viewable at once.
  const [fileUrl, setFileUrl] = useState(null);
  useEffect(() => {
    if (!file) { setFileUrl(null); return undefined; }
    const url = URL.createObjectURL(file);
    setFileUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const [reason, setReason] = useState('');
  const [reviewerStaffId, setReviewerStaffId] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // The single corrected file wrapped once per selection — a fresh array
  // every render would wipe the hooks' outcome the moment it appeared.
  const detectFiles = useMemo(() => (file ? [file] : []), [file]);

  // Fallback lane when the server has no AI configured: the stand-alone
  // auto-detect button on the revise-sign-off switch (the original flow).
  const detect = useFsAutoDetect({
    files: detectFiles,
    onDetected: (d) => { setReviseSignOff(true); setSigningDate(d); },
    disabled: saving || aiEnabled,
  });

  // The mandatory AI review gate — same as the Signed Financial Statements form
  // (docs/modules/workflow-fs.md §6/§7): one button runs detection + the AI
  // checks on the corrected document; a pass approves the amendment
  // automatically (no date field, no reviewer), anything else reveals both.
  const ai = useFsAiReview({
    enabled: aiEnabled,
    engagementId: engagementId ? parseInt(engagementId, 10) : null,
    files: detectFiles,
    onDetected: setSigningDate,
    disabled: saving,
  });
  const manualLane = !aiEnabled || ai.needsReviewer;

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchJSON('/api/workflow/fsa/options');
        setEngagements(data.engagements || []);
        setAiEnabled(data.aiReview?.enabled === true);
      } catch (e) { setError(e.message); }
    })();
  }, []);

  const picked = engagements.find((e) => String(e.id) === String(engagementId)) || null;

  // Manual lane on the AI flow: the date arrives pre-filled — the detected
  // date when there is one, else the current sign-off (re-stamping the same
  // value is a no-op) — and stays editable.
  useEffect(() => {
    if (aiEnabled && ai.needsReviewer && !signingDate) {
      setSigningDate(ai.detectedDate || picked?.signOffDate || '');
    }
  }, [aiEnabled, ai.needsReviewer, ai.detectedDate, picked, signingDate]);

  const pickEngagement = (id) => {
    setEngagementId(id);
    setReviseSignOff(false);
    setSigningDate('');
  };

  // Everything still empty, keyed to the control it belongs to — after the
  // first Submit the page marks those controls and lists them in the banner.
  // AI flow: the review itself is the gap until it reaches a verdict; a pass
  // needs neither date nor reviewer; the manual lane needs both. Fallback
  // flow (AI off): the revised date counts only while its switch is on.
  const gaps = useMemo(() => {
    const g = [];
    if (!engagementId) g.push({ key: 'engagement', label: 'Engagement' });
    if (aiEnabled && !ai.needsReviewer && !ai.passed) g.push({ key: 'aiReview', label: 'AI Review' });
    if (aiEnabled && ai.needsReviewer && !signingDate) g.push({ key: 'signingDate', label: 'Audit Report Signing Date' });
    if (!aiEnabled && reviseSignOff && !signingDate) g.push({ key: 'signingDate', label: 'Revised Sign-Off Date' });
    if (manualLane && !reviewerStaffId) g.push({ key: 'reviewerName', label: 'Reviewer' });
    if (!reason.trim()) g.push({ key: 'reason', label: 'Reason' });
    return g;
  }, [engagementId, reviseSignOff, signingDate, reviewerStaffId, reason,
    aiEnabled, ai.needsReviewer, ai.passed, manualLane]);
  const validation = useFormValidation(gaps);

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    setError(null);
    if (!file) { setError('Upload the corrected signed document.'); return; }
    setSaving(true);
    try {
      const body = new FormData();
      body.set('engagementId', parseInt(engagementId, 10));
      body.set('docType', 'fs');
      // AI pass files the DETECTED date via the voucher; the manual lane files
      // the (pre-filled, editable) field value; the fallback flow keeps the
      // opt-in switch semantics.
      if (aiEnabled && ai.passed) {
        body.set('aiReviewId', String(ai.aiReviewId));
        body.set('signingDate', ai.detectedDate);
      } else {
        if (aiEnabled ? signingDate : (reviseSignOff && signingDate)) body.set('signingDate', signingDate);
        body.set('reviewerStaffId', reviewerStaffId);
      }
      body.set('reason', reason.trim());
      if (file) body.set('file', file);
      const data = await fetchJSON('/api/workflow/fsa', { method: 'POST', body });
      navigate(`/workflow/fsa/${data.request.id}`);
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormShell
      backTo="/workflow/fsa"
      backLabel="Signed Financial Statements Amendment"
      title="New Signed Financial Statements Amendment Request"
      subtitle="Correct approved signed financial statements."
      error={error || validation.error}
      onSubmit={submit}
      submitting={saving}
      submitDisabled={engagementBlockReasons(picked ? [picked] : [], 'fsa').length > 0 || detect.busy || ai.busy}
    >
      <FormValidationProvider value={validation.providerValue}>
        <FormSection step={1} title="Select the Engagement">
          <Field label="Engagement" required field="engagement" hint="Only engagements with a Signed Financial Statements record are listed.">
            <SearchSelect
              value={engagementId}
              onChange={pickEngagement}
              options={engagements.map((e) => ({ value: e.id, label: [`${shortEngagementName(e.engagementName)} — FYE ${formatDateShort(e.yearEnd)}`, engagementTag(e.serviceProvided, e.engagementSeq, e.serviceDescription)].filter(Boolean).join(' · ') }))}
              placeholder="Search engagement…"
            />
            <EngagementBlockers selected={picked ? [picked] : []} flow="fsa" />
            {picked && (
              <div className="mt-2 border border-slate-200 rounded-lg p-3 bg-slate-50 text-sm">
                <span className={`${detailLabel} mr-2`}>Current Sign-Off</span>
                {picked.signOffDate ? formatDate(picked.signOffDate) : '—'}
              </div>
            )}
          </Field>
        </FormSection>

        <FormSection step={2} title="Corrected Document">
          {!engagementId ? (
            <p className="text-sm text-slate-500">Select an engagement above first.</p>
          ) : (
            <>
              {/* Old vs new, side by side — left the document being replaced
                  (the record's CURRENT file; an earlier amendment may have
                  superseded the original request's copy — served through the
                  member-scoped engagement attachment route), right the
                  replacement being uploaded, viewable at once via a local
                  object URL. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">Current Signed Financial Statements on File (Being Replaced)</h3>
                  {picked?.currentDocs?.fs ? (
                    <p className="text-sm">
                      <a
                        href={`${API_BASE_URL}/api/my/engagements/${picked.id}/attachments/fs/${picked.currentDocs.fs.fileId}`}
                        className="text-blue-600 hover:text-blue-800 underline break-all"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {picked.currentDocs.fs.originalName}
                      </a>
                      {picked.currentDocs.fs.requestNo && (
                        <span className="text-xs text-slate-500 ml-2">({picked.currentDocs.fs.requestNo})</span>
                      )}
                    </p>
                  ) : (
                    <p className="text-sm text-slate-500">—</p>
                  )}
                </div>

                <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3">
                  <h3 className="text-xs font-semibold text-blue-700 uppercase mb-2">
                    Corrected Signed Financial Statements Document <InfoTip text="This replaces the current document on the left." />
                  </h3>
                  <FilePicker
                    kind="docsAndImages"
                    disabled={detect.busy || ai.busy}
                    onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ''; }}
                    showSelection={false}
                  />
                  {file && (
                    <p className="flex items-center gap-2 text-sm text-slate-700 mt-1.5">
                      <span className="break-all min-w-0">{file.name}</span>
                      {fileUrl && (
                        <a href={fileUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline hover:text-blue-800 whitespace-nowrap">View</a>
                      )}
                      <button
                        type="button"
                        onClick={() => setFile(null)}
                        title={`Remove ${file.name}`}
                        aria-label={`Remove ${file.name}`}
                        className={fileChipRemove}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </p>
                  )}
                  <p className="text-xs text-slate-500 mt-1.5">
                    {hintOf('docsOnly')} Upload a PDF to enable {aiEnabled ? 'the AI review' : 'signing-date auto-detection'}.
                  </p>
                </div>
              </div>

              {aiEnabled ? (
                <>
                  {/* One click detects the signing date on the corrected
                      document, then the AI checks the handwritten firm code
                      and the BizFile match; a pass approves the amendment
                      automatically and re-stamps the detected date. */}
                  <Field label="AI Review" required field="aiReview" hint="Mandatory before submitting.">
                    {ai.panel}
                  </Field>
                  {/* Manual lane only: the requester confirms the date
                      themselves (pre-filled with the detected date, or the
                      current sign-off). Approval re-stamps it. */}
                  {ai.needsReviewer && (
                    <Field
                      label="Audit Report Signing Date"
                      required
                      field="signingDate"
                      hint={`Pre-filled with ${ai.detectedDate ? 'the date detected from the corrected document' : 'the current sign-off'} — verify it against the corrected document; approval re-stamps it.`}
                    >
                      <input type="date" value={signingDate} onChange={(e) => setSigningDate(e.target.value)} className={inputCls} />
                    </Field>
                  )}
                </>
              ) : (
                <ReviseSignOffField
                  reviseSignOff={reviseSignOff}
                  onToggle={setReviseSignOff}
                  signingDate={signingDate}
                  onSigningDateChange={setSigningDate}
                  currentSignOffDate={picked?.signOffDate}
                  detect={detect}
                />
              )}
            </>
          )}
        </FormSection>

        <FormSection step={3} title="Reason">
          <Field label="Reason" required field="reason">
            <textarea rows="2" value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} />
          </Field>
        </FormSection>

        {/* Manual lane only — a passing AI review needs no reviewer, so the
            whole section stays hidden. */}
        {manualLane && (
          <FormSection step={4} title="Reviewer">
            <Field label="Reviewer" required field="reviewerName"
              hint="You pick who reviews this amendment, from the firm's Partners.">
              <ReviewerSelect value={reviewerStaffId} onChange={setReviewerStaffId} grade="partner" />
            </Field>
          </FormSection>
        )}
      </FormValidationProvider>
    </FormShell>
  );
};

export default WorkflowFsaNew;
